// Shared by both signature pads: signature-pad/main.js (custom intake documents and PDF intake
// documents) and users/elements/signature_pad_element.ts (service log entries, sign-by-link and
// user signatures).
//
// signature_pad stores strokes in the canvas' own coordinate space and never moves them when the
// canvas is resized. A pad that gets narrower therefore leaves part of the signature sitting
// outside it: invisible on screen, and — because signature_pad sizes the exported SVG's viewBox
// (and we size the trimmed PNG) from the canvas' current dimensions — silently cropped out of what
// we save. Keeping the strokes as they were drawn and scaling a copy of them to fit the pad is what
// keeps the saved signature equal to the one the signer is looking at.

// Point-group keys that carry a pen width and therefore have to scale with the geometry, otherwise
// a shrunk signature keeps full-size strokes and reads as a fatter, blunter version of itself.
const WIDTH_KEYS = ['dotSize', 'minWidth', 'maxWidth'];

/**
 * Multiply every point, and the pen widths that go with it, by `factor`.
 *
 * @param {Array} groups point groups as returned by SignaturePad#toData
 * @param {number} factor
 * @returns {Array} new point groups; the originals are left untouched
 */
export function scaleStrokes(groups, factor) {
  if (factor === 1) return groups;

  return groups.map((group) => {
    const scaled = {
      ...group,
      points: group.points.map((point) => ({ ...point, x: point.x * factor, y: point.y * factor })),
    };

    // Only touch the keys the group actually carries. signature_pad reads these with
    // `'dotSize' in group`, so adding the key with an undefined value would blank out its default.
    WIDTH_KEYS.forEach((key) => {
      if (typeof scaled[key] === 'number') scaled[key] *= factor;
    });

    return scaled;
  });
}

/**
 * How much the strokes have to shrink for every point of ink to fit inside a pad of `width` by
 * `height`. Never greater than 1: growing the pad leaves the signature at the size it was signed
 * at, which is what the signer expects to see, while shrinking is the only way to keep all of it
 * inside the pad and therefore inside what gets saved.
 *
 * The fit is measured against the ink's own extent, not the size of the pad it was drawn in. The
 * strokes on a pad are not all born in one coordinate space: widen the pad (rotate to landscape)
 * and the existing strokes keep their old coordinates while a newly dotted i lands in the wide
 * pad's space. A pad-size ratio would shrink all of that by the full ratio on the way back even
 * though every point already fit — permanently, because the shrunk copy is what gets adopted as
 * the signature-as-drawn the next time a stroke is captured. Ink that fits is left alone; ink
 * that does not is shrunk exactly enough to reach the pad's edge.
 *
 * @param {Array|null} strokes point groups as returned by SignaturePad#toData
 * @param {number} width current pad width in CSS pixels
 * @param {number} height current pad height in CSS pixels
 * @returns {number}
 */
export function fitFactor(strokes, width, height) {
  if (!strokes || strokes.length === 0) return 1;
  // A pad reporting no size at all is a layout that has not settled: a container mid-drag, a tab
  // that is not visible yet. Scaling to fit that would multiply the signature down to a single
  // point, and if anything then adopts the result as the signature-as-drawn it is gone for good.
  // Leave the strokes alone and let the next real resize place them.
  if (!(width > 0) || !(height > 0)) return 1;

  let maxX = 0;
  let maxY = 0;
  strokes.forEach((group) => {
    group.points.forEach((point) => {
      if (point.x > maxX) maxX = point.x;
      if (point.y > maxY) maxY = point.y;
    });
  });

  return Math.min(maxX > 0 ? width / maxX : 1, maxY > 0 ? height / maxY : 1, 1);
}
